import { useCallback, useEffect, useRef, useState } from 'react'

import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  type ConversationCartComponent,
  type ConversationCartItem,
} from '../../value-objects/ConversationTurnItem'
import {
  ConversationStreamStatuses,
  type ConversationStreamStatusEnum,
} from '../useConversationStream'

const QUANTITY_DEBOUNCE_MS = 1500
const MIN_QUANTITY = 1

export interface DecoratedCart {
  items: ConversationCartItem[]
  total: number
  currency: CurrencyName
  disabled: boolean
}

export interface UseCartResult {
  decorate: (cart: ConversationCartComponent) => DecoratedCart
  increment: (itemId: string) => void
  decrement: (itemId: string) => void
  remove: (itemId: string) => void
  continueToOrderRequest: () => void
}

interface UseCartArgs {
  cart: Nullable<ConversationCartComponent>
  status: ConversationStreamStatusEnum
  sendSilentMessage: (message: string) => boolean
  onContinued: () => void
}

export const useCart = ({
  cart,
  status,
  sendSilentMessage,
  onContinued,
}: UseCartArgs): UseCartResult => {
  const intl = useProcurifyIntl()

  const [pendingQuantities, setPendingQuantities] = useState<
    Record<string, number>
  >({})
  const [pendingRemovalIds, setPendingRemovalIds] = useState<string[]>([])
  const [hasPendingTurn, setHasPendingTurn] = useState(false)

  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const cartKeyRef = useRef<Nullable<string>>(null)
  const pendingContinueRef = useRef(false)

  // The cart only changes by sending a silent turn and waiting for the agent to
  // stream back a fresh CART envelope. A new cart id or version reconciles the
  // predicted changes shown while the turn was in flight and re-enables the
  // controls — keying on both guards against a reused version on a new cart.
  useEffect(() => {
    if (!cart) return
    const cartKey = `${cart.cartId}:${cart.version}`
    if (cartKeyRef.current === cartKey) return
    cartKeyRef.current = cartKey
    setPendingQuantities({})
    setPendingRemovalIds([])
    setHasPendingTurn(false)
  }, [cart])

  // Redirect once the silent "continue" turn has settled. A failed turn clears
  // the pending flag so a later unrelated DONE never navigates away.
  useEffect(() => {
    if (!pendingContinueRef.current) return
    if (status === ConversationStreamStatuses.DONE) {
      pendingContinueRef.current = false
      onContinued()
    } else if (status === ConversationStreamStatuses.ERROR) {
      pendingContinueRef.current = false
      setHasPendingTurn(false)
    }
  }, [status, onContinued])

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach(clearTimeout)
    },
    []
  )

  const decorate = useCallback(
    (component: ConversationCartComponent): DecoratedCart => {
      // Only the latest cart envelope is interactive — older snapshots in the
      // transcript stay disabled so their controls never drive the live cart.
      const isLatest =
        cart !== null &&
        component.cartId === cart.cartId &&
        component.version === cart.version

      return {
        items: component.items
          .filter((item) => !(isLatest && pendingRemovalIds.includes(item.id)))
          .map((item) => ({
            ...item,
            quantity: isLatest
              ? (pendingQuantities[item.id] ?? item.quantity)
              : item.quantity,
          })),
        total: component.total,
        currency: component.currency,
        disabled:
          !isLatest ||
          hasPendingTurn ||
          status === ConversationStreamStatuses.STREAMING,
      }
    },
    [cart, pendingQuantities, pendingRemovalIds, hasPendingTurn, status]
  )

  const scheduleQuantityRequest = useCallback(
    (itemId: string, name: string, quantity: number) => {
      clearTimeout(timersRef.current[itemId])
      timersRef.current[itemId] = setTimeout(() => {
        // Skipped while a turn is already streaming — leave the controls
        // enabled so the change can be retried instead of locking the cart.
        setHasPendingTurn(
          sendSilentMessage(
            intl.formatMessage(messages.setQuantity, { name, quantity })
          )
        )
      }, QUANTITY_DEBOUNCE_MS)
    },
    [intl, sendSilentMessage]
  )

  const requestQuantityChange = useCallback(
    (itemId: string, delta: number) => {
      const item = (cart?.items ?? []).find(
        (candidate) => candidate.id === itemId
      )
      if (!item) return

      const currentQuantity = pendingQuantities[itemId] ?? item.quantity
      const nextQuantity = Math.max(MIN_QUANTITY, currentQuantity + delta)
      if (nextQuantity === currentQuantity) return

      setPendingQuantities((prev) => ({ ...prev, [itemId]: nextQuantity }))
      scheduleQuantityRequest(itemId, item.name, nextQuantity)
    },
    [cart, pendingQuantities, scheduleQuantityRequest]
  )

  const increment = useCallback(
    (itemId: string) => requestQuantityChange(itemId, 1),
    [requestQuantityChange]
  )

  const decrement = useCallback(
    (itemId: string) => requestQuantityChange(itemId, -1),
    [requestQuantityChange]
  )

  const remove = useCallback(
    (itemId: string) => {
      const item = (cart?.items ?? []).find(
        (candidate) => candidate.id === itemId
      )
      if (!item) return

      if (
        !sendSilentMessage(
          intl.formatMessage(messages.removeItem, { name: item.name })
        )
      )
        return

      clearTimeout(timersRef.current[itemId])
      setPendingRemovalIds((prev) => [...prev, itemId])
      setHasPendingTurn(true)
    },
    [cart, intl, sendSilentMessage]
  )

  const continueToOrderRequest = useCallback(() => {
    if (!sendSilentMessage(intl.formatMessage(messages.continueToOrderRequest)))
      return
    pendingContinueRef.current = true
    setHasPendingTurn(true)
  }, [intl, sendSilentMessage])

  return {
    decorate,
    increment,
    decrement,
    remove,
    continueToOrderRequest,
  }
}
